// Data seam: sphinx is single-tenant. There is one implicit project and one
// operator, so projects:* collapses to constants and every member / invite /
// multi-project / org mutation is a resolved no-op.
//
// Decisions:
// - useMyProjects → [DEFAULT_PROJECT] (static)
// - useProjectMembersQuery → the operator as sole owner (static)
// - createProject / ensureDefaultProject → resolve DEFAULT_PROJECT_ID
// - updateProject / patchProjectDefaultConnection / deleteProject / member +
//   invite mutations → no-ops (project settings UI is deleted)
import { OPERATOR_USER } from "./user";

export const DEFAULT_PROJECT_ID = "default";

export const DEFAULT_PROJECT = {
  _id: DEFAULT_PROJECT_ID,
  name: "Sphinx",
  description: "",
  servers: {} as Record<string, unknown>,
  canDeleteProject: false,
  organizationId: "default",
  visibility: "private" as const,
  ownerId: OPERATOR_USER._id,
  createdAt: 0,
  updatedAt: 0,
};

const PROJECTS = [DEFAULT_PROJECT];

const OPERATOR_MEMBER = {
  _id: "member-operator",
  projectId: DEFAULT_PROJECT_ID,
  organizationId: "default",
  userId: OPERATOR_USER._id,
  email: OPERATOR_USER.email,
  role: "owner" as const,
  projectRole: "admin" as const,
  canChangeRole: false,
  addedBy: OPERATOR_USER._id,
  addedAt: 0,
  isOwner: true,
  isPending: false,
  hasAccess: true,
  accessSource: "project" as const,
  canRemove: false,
  user: { name: OPERATOR_USER.name, email: OPERATOR_USER.email, imageUrl: "" },
};

const MEMBERS = { members: [OPERATOR_MEMBER], canManageMembers: true };

/** projects:getMyProjects */
export function useMyProjects(args: Record<string, never> | "skip") {
  return args === "skip" ? undefined : PROJECTS;
}

/** projects:getProjectMembers */
export function useProjectMembersQuery(args: { projectId: string } | "skip") {
  return args === "skip" ? undefined : MEMBERS;
}

const resolveDefaultProjectId = async (_args?: unknown): Promise<string> =>
  DEFAULT_PROJECT_ID;
const noop = async (_args?: unknown): Promise<void> => {};

/** projects:createProject — single project: always the default. */
export function useCreateProjectMutation() {
  return resolveDefaultProjectId;
}

/** projects:ensureDefaultProject */
export function useEnsureDefaultProjectMutation() {
  return resolveDefaultProjectId;
}

/** projects:mergeServersIntoExistingProject — servers are global; no-op. */
export function useMergeServersIntoExistingProjectMutation() {
  return noop;
}

const PROJECT_MUTATIONS = {
  createProject: resolveDefaultProjectId,
  ensureDefaultProject: resolveDefaultProjectId,
  updateProject: noop,
  patchProjectDefaultConnection: noop,
  deleteProject: noop,
  inviteProjectMember: noop,
  removeProjectMember: noop,
  updateProjectMemberRole: noop,
  updateProjectInviteRole: noop,
};

export function useProjectMutations() {
  return PROJECT_MUTATIONS;
}
